const geoLog = (...args) => console.info('[geo-redirect]', ...args)

const GEO_LOOKUP_TIMEOUT_MS = 5000

const GEO_US_CODE = 'US'

const normalizeCountry = (value) => {
  const country = String(value == null ? '' : value).trim().toUpperCase()
  return /^[A-Z]{2}$/.test(country) ? country : null
}

class GeoRedirectModal extends HTMLElement {
  constructor() {
    super()
    this.overlay = this.querySelector('[data-geo-redirect-overlay]')
    this.dialog = this.querySelector('[data-geo-redirect-dialog]')
    this.closeButton = this.querySelector('[data-geo-redirect-close]')
    this.ctaLink = this.querySelector('[data-geo-redirect-cta]')
    this._inertNodes = []
    this._handleKeydown = this._handleKeydown.bind(this)
    this._handleFocusIn = this._handleFocusIn.bind(this)
    this._handleBodyMutation = this._handleBodyMutation.bind(this)
    this._bodyObserver = new MutationObserver(this._handleBodyMutation)
  }

  connectedCallback() {
    if (!this.dialog || !this.closeButton) {
      geoLog('markup incomplete (dialog or close button missing) - not starting')
      return
    }

    if (this.dismissed) {
      geoLog('visitor already dismissed the popup this session (f4_geo_dismissed_session) - removing, no lookup')
      this.remove()
      return
    }

    if (this._started) return
    this._started = true

    this.closeButton.addEventListener('click', () => this.closeModal())
    this.ctaLink?.addEventListener('click', () => this.closeModal())
    // Deliberately no overlay/backdrop-click and no Escape handling: this modal
    // requires an explicit choice (close button or the CTA), so a stray click
    // outside it or a reflex Escape press must not dismiss it. The close
    // button remains fully keyboard-reachable (see _handleKeydown's Tab
    // cycling below), so this doesn't reintroduce a keyboard trap - there's
    // still a real, operable way out, just not an incidental one.

    this.detectCountry().then((country) => {
      if (!this.isConnected) {
        geoLog('element was removed before the lookup finished - nothing to do')
        return
      }
      if (country && country !== GEO_US_CODE) {
        geoLog(`country "${country}" is not US - opening popup`)
        // Opening itself must not depend on requestAnimationFrame: rAF is throttled/suspended
        // while the document is hidden (a backgrounded tab, tab groups, session restore), which
        // would otherwise mean the popup silently never appears for that visitor.
        this.openModal()
      } else {
        geoLog(country ? `country is "${country}" - no popup for US visitors` : 'no country resolved - staying silent, no popup')
        this.remove()
      }
    })
  }

  // Resolves to a 2-letter country code, or null on any failure; cached for 24h.
  async detectCountry() {
    const cached = this.readCachedCountry()
    if (cached) {
      geoLog(`using cached country "${cached}" (f4_geo_country, 24h cache) - clear it in localStorage to force a fresh lookup`)
      return cached
    }

    const endpoint = this.dataset.geoEndpoint
    if (!endpoint) {
      geoLog('no data-geo-endpoint on <geo-redirect-modal> - cannot look up country')
      return null
    }

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS)
    try {
      geoLog('fetching country from', endpoint)
      const response = await fetch(endpoint, { signal: controller.signal })
      const raw = await response.text()
      geoLog(`endpoint responded HTTP ${response.status}, raw output: ${JSON.stringify(raw.slice(0, 200))}`)
      if (!response.ok) return null
      const country = normalizeCountry(raw)
      if (!country) {
        geoLog('response is not a 2-letter country code - ignoring')
        return null
      }
      this.cacheCountry(country)
      return country
    } catch (e) {
      geoLog(e && e.name === 'AbortError' ? `lookup timed out after ${GEO_LOOKUP_TIMEOUT_MS / 1000}s` : `lookup failed: ${e && e.message} (check CORS / network tab)`)
      return null
    } finally {
      clearTimeout(timer)
    }
  }

  readCachedCountry() {
    try {
      const cached = JSON.parse(localStorage.getItem('f4_geo_country'))
      const country = cached && normalizeCountry(cached.country)
      if (country && Date.now() - cached.timestamp < 24 * 60 * 60 * 1000) {
        return country
      }
    } catch (e) {
      // storage unavailable or malformed
    }
    return null
  }

  cacheCountry(country) {
    try {
      localStorage.setItem('f4_geo_country', JSON.stringify({ country, timestamp: Date.now() }))
    } catch (e) {
      // storage unavailable
    }
  }

  get dismissed() {
    try {
      // Session-only on purpose; the old permanent `f4_geo_dismissed` flag is ignored
      return sessionStorage.getItem('f4_geo_dismissed_session') === 'true'
    } catch (e) {
      return false
    }
  }

  // Other on-page popups (Gorgias chat, Klaviyo forms, cookie consent, ...)
  // must stay closeable while this modal is open - never inert them, and
  // never yank focus back out of them. `.needsclick` is this theme's own
  // existing convention for exactly that (see custom.css.liquid's
  // `[data-scroll-locked] .needsclick` rule, already used by both Klaviyo
  // and Gorgias on this site); the id/class checks are a belt-and-braces
  // fallback for widgets that don't carry that class on every node.
  _isExempt(node) {
    if (!node || node.nodeType !== 1 || typeof node.closest !== 'function') return false
    return !!node.closest(
      '.needsclick, #gorgias-chat-container, [id*="gorgias" i], [class*="gorgias" i], [id*="klaviyo" i], [class*="klaviyo" i]'
    )
  }

  // Belt-and-braces containment: even if some element outside the dialog
  // wasn't (yet) marked inert - e.g. a third-party widget (cookie consent,
  // chat) that mounts itself into <body> asynchronously, after our one-time
  // inert snapshot - focus must never actually land there while open.
  _handleFocusIn(event) {
    if (this.getAttribute('aria-open') !== 'true') return
    if (this.dialog.contains(event.target)) return
    if (this._isExempt(event.target)) return

    const focusable = this.getFocusableElements()
    ;(focusable[0] || this.dialog).focus()
  }

  // Widgets that mount themselves into <body> after we've already opened
  // (and taken our inert snapshot) would otherwise sit outside the trap
  // entirely - inert them too as they appear.
  _handleBodyMutation(mutations) {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1 || node === this || node.parentElement !== document.body) return
        if (this._isExempt(node)) return
        if (!node.hasAttribute('inert')) {
          node.setAttribute('inert', '')
          this._inertNodes.push(node)
        }
      })
    })
  }

  _handleKeydown(event) {
    if (event.key !== 'Tab') return

    const focusable = this.getFocusableElements()
    if (!focusable.length) return

    // Drive Tab entirely ourselves rather than only handling the first/last
    // boundary: some third-party widgets on the page (e.g. a cookie-consent
    // banner) use a positive tabindex, which wins native tab order from
    // *anywhere* on the page regardless of where focus currently is. Letting
    // the browser's default Tab action run at all risks jumping straight to
    // one of those instead of the next control inside this dialog.
    event.preventDefault()

    const currentIndex = focusable.indexOf(document.activeElement)
    let nextIndex
    if (event.shiftKey) {
      nextIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1
    } else {
      nextIndex = currentIndex === -1 || currentIndex === focusable.length - 1 ? 0 : currentIndex + 1
    }
    focusable[nextIndex].focus()
  }

  getFocusableElements() {
    return Array.from(
      this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    )
  }

  _setInertState(isOpen) {
    const nodes = Array.from(document.querySelectorAll('body > *'))
    nodes.forEach((node) => {
      if (node === this || this._isExempt(node)) return
      if (isOpen) {
        if (!node.hasAttribute('inert')) {
          node.setAttribute('inert', '')
          this._inertNodes.push(node)
        }
      }
    })
    if (!isOpen) {
      this._inertNodes.forEach((node) => node.removeAttribute('inert'))
      this._inertNodes = []
    }
  }

  rememberChoice() {
    try {
      sessionStorage.setItem('f4_geo_dismissed_session', 'true')
    } catch (e) {
      // storage unavailable (e.g. private mode) - fail silently, modal just reappears next time
    }
  }

  openModal() {
    this.lastFocusedElement = document.activeElement
    this.dialog.removeAttribute('inert')
    this.dialog.setAttribute('aria-hidden', 'false')
    this.overlay.hidden = false
    this._setInertState(true)
    document.addEventListener('keydown', this._handleKeydown)
    document.addEventListener('focusin', this._handleFocusIn)
    this._bodyObserver.observe(document.body, { childList: true })
    this.setAttribute('aria-open', 'true')
    document.documentElement.setAttribute('geo-redirect-modal-open', 'true')

    requestAnimationFrame(() => {
      this.dialog.focus()
    })
  }

  closeModal() {
    if (this._closed) return
    this._closed = true

    this.rememberChoice()
    this.setAttribute('aria-open', 'false')
    this.dialog.setAttribute('aria-hidden', 'true')
    this._setInertState(false)
    document.removeEventListener('keydown', this._handleKeydown)
    document.removeEventListener('focusin', this._handleFocusIn)
    this._bodyObserver.disconnect()
    document.documentElement.removeAttribute('geo-redirect-modal-open')

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus()
    } else {
      document.querySelector('.skip-link')?.focus()
    }

    setTimeout(() => {
      this.overlay.hidden = true
      this.dialog.setAttribute('inert', '')
      this.remove()
    }, 300)
  }
}

customElements.define('geo-redirect-modal', GeoRedirectModal)
